/**
 * MonitoringControlsPanel — admin tuning of RAELA Function 1.
 *
 * Member monitoring stays always-on globally (V2 §13). Admins can:
 *   - Per community room: off / standard / strict / shadow
 *   - Per member override: same modes, plus a temporary 'shadow window'
 *     that expires automatically (e.g. 72h close-watch after a strike).
 *
 * Also surfaces training-corpus stats so we know how many members have
 * opted in to fine-tuning RAELA on real community language.
 */
import { useState } from "react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  useRoomMonitoring,
  useUserMonitoringSearch,
  useTrainingStats,
  type MonitoringMode,
} from "@/lib/moderation/useMonitoringControls";
import MonitorThresholdsPanel from "@/components/founder/MonitorThresholdsPanel";
import { Brain, Eye, ShieldCheck, ShieldOff, Zap } from "lucide-react";

const MODE_META: Record<
  MonitoringMode,
  { label: string; icon: React.ComponentType<{ className?: string }>; tone: string; desc: string }
> = {
  off: {
    label: "Off",
    icon: ShieldOff,
    tone: "text-muted-foreground",
    desc: "No classification, no logs. Sandbox only.",
  },
  standard: {
    label: "Standard",
    icon: ShieldCheck,
    tone: "text-foreground",
    desc: "Default — classify, flag, escalate per spec.",
  },
  strict: {
    label: "Strict",
    icon: Zap,
    tone: "text-amber-600",
    desc: "Promote borderline 'info' verdicts to 'warn'. High-risk rooms.",
  },
  shadow: {
    label: "Shadow",
    icon: Eye,
    tone: "text-sky-600",
    desc: "Observe + log only. No actions, no strikes. Tuning new rules.",
  },
};

const ModeSelect = ({
  value,
  onChange,
  allowNull = false,
}: {
  value: MonitoringMode | null;
  onChange: (v: MonitoringMode | null) => void;
  allowNull?: boolean;
}) => (
  <Select
    value={value ?? "__inherit"}
    onValueChange={(v) => onChange(v === "__inherit" ? null : (v as MonitoringMode))}
  >
    <SelectTrigger className="w-44">
      <SelectValue />
    </SelectTrigger>
    <SelectContent>
      {allowNull && <SelectItem value="__inherit">Inherit (default)</SelectItem>}
      <SelectItem value="standard">Standard</SelectItem>
      <SelectItem value="strict">Strict</SelectItem>
      <SelectItem value="shadow">Shadow (observe)</SelectItem>
      <SelectItem value="off">Off</SelectItem>
    </SelectContent>
  </Select>
);

const RoomsTab = () => {
  const { rooms, loading, error, setMode } = useRoomMonitoring();
  if (loading) return <p className="text-sm text-muted-foreground">Loading…</p>;
  if (error) return <p className="text-sm text-destructive">{error}</p>;

  const grouped = rooms.reduce<Record<string, typeof rooms>>((acc, r) => {
    (acc[r.app] ??= []).push(r);
    return acc;
  }, {});

  return (
    <div className="space-y-6">
      {Object.entries(grouped).map(([app, list]) => (
        <Card key={app} className="p-4">
          <h4 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
            {app}
          </h4>
          <ul className="mt-3 space-y-2">
            {list.map((room) => {
              const meta = MODE_META[room.monitoring_mode];
              const Icon = meta.icon;
              return (
                <li
                  key={room.id}
                  className="flex flex-wrap items-center gap-3 rounded-lg border border-border px-3 py-2"
                >
                  <Icon className={`h-4 w-4 ${meta.tone}`} />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium truncate">{room.name}</p>
                    <p className="text-xs text-muted-foreground">{meta.desc}</p>
                  </div>
                  <ModeSelect
                    value={room.monitoring_mode}
                    onChange={(v) => v && setMode(room.id, v)}
                  />
                </li>
              );
            })}
          </ul>
        </Card>
      ))}
    </div>
  );
};

const UsersTab = () => {
  const { results, loading, search, setOverride, setShadowWindow } =
    useUserMonitoringSearch();
  const [q, setQ] = useState("");

  return (
    <div className="space-y-4">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          void search(q);
        }}
        className="flex gap-2"
      >
        <Input
          placeholder="Search members by name or username…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
        <Button type="submit" disabled={loading}>Search</Button>
      </form>

      {!loading && results.length === 0 && (
        <p className="text-sm text-muted-foreground">
          {q ? "No members match." : "No active per-user overrides. Search to apply one."}
        </p>
      )}

      <ul className="space-y-2">
        {results.map((u) => {
          const shadow = u.shadow_monitoring_until && new Date(u.shadow_monitoring_until) > new Date();
          return (
            <li key={u.id}>
              <Card className="p-3 space-y-3">
                <div className="flex flex-wrap items-center gap-2">
                  <p className="text-sm font-medium">
                    {u.display_name ?? u.username ?? u.id.slice(0, 8)}
                  </p>
                  {u.username && (
                    <span className="text-xs text-muted-foreground">@{u.username}</span>
                  )}
                  {u.ai_training_opt_in && (
                    <Badge variant="secondary" className="ml-auto">
                      <Brain className="mr-1 h-3 w-3" /> Training opt-in
                    </Badge>
                  )}
                </div>

                <div className="flex flex-wrap items-end gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs">Monitoring override</Label>
                    <ModeSelect
                      value={u.monitoring_mode_override}
                      onChange={(v) => setOverride(u.id, v)}
                      allowNull
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Shadow watch window</Label>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setShadowWindow(u.id, 24)}
                      >
                        24h
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setShadowWindow(u.id, 72)}
                      >
                        72h
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setShadowWindow(u.id, null)}
                        disabled={!shadow}
                      >
                        Clear
                      </Button>
                    </div>
                    {shadow && (
                      <p className="text-xs text-muted-foreground">
                        Active until {new Date(u.shadow_monitoring_until!).toLocaleString()}
                      </p>
                    )}
                  </div>
                </div>
              </Card>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

const TrainingTab = () => {
  const { stats, loading } = useTrainingStats();
  if (loading || !stats) return <p className="text-sm text-muted-foreground">Loading…</p>;
  return (
    <Card className="p-6 space-y-4">
      <div className="flex items-center gap-2">
        <Brain className="h-5 w-5" />
        <h4 className="text-base font-semibold">Community fine-tune corpus</h4>
      </div>
      <p className="text-sm text-muted-foreground">
        Members who toggle <span className="text-foreground">Contribute to AI training</span>{" "}
        in their account contribute classified messages here. Samples are stored verbatim
        with the verdict so RAELA can be fine-tuned on real community language.
      </p>
      <div className="grid grid-cols-3 gap-3">
        <div className="rounded-xl border border-border p-4">
          <p className="text-xs text-muted-foreground">Opted-in members</p>
          <p className="mt-1 text-2xl font-semibold">{stats.optedInMembers}</p>
        </div>
        <div className="rounded-xl border border-border p-4">
          <p className="text-xs text-muted-foreground">Total samples</p>
          <p className="mt-1 text-2xl font-semibold">{stats.totalSamples}</p>
        </div>
        <div className="rounded-xl border border-border p-4">
          <p className="text-xs text-muted-foreground">Last 24h</p>
          <p className="mt-1 text-2xl font-semibold">{stats.last24h}</p>
        </div>
      </div>
      <p className="text-xs text-muted-foreground">
        Export the corpus from the database (<code>raela_training_samples</code>) when ready
        to ship a fine-tune batch.
      </p>
    </Card>
  );
};

export const MonitoringControlsPanel = () => (
  <div className="space-y-4">
    <div>
      <h3 className="text-base font-medium">RAELA monitoring controls</h3>
      <p className="text-sm text-muted-foreground mt-1">
        Function 1 stays on globally. Use these dials to tune sensitivity per
        community room or apply a temporary watch on a specific member. Members
        cannot disable monitoring on themselves.
      </p>
    </div>
    <Tabs defaultValue="rooms" className="space-y-4">
      <TabsList>
        <TabsTrigger value="rooms">Per room</TabsTrigger>
        <TabsTrigger value="users">Per user</TabsTrigger>
        <TabsTrigger value="thresholds">Thresholds</TabsTrigger>
        <TabsTrigger value="training">Training corpus</TabsTrigger>
      </TabsList>
      <TabsContent value="rooms"><RoomsTab /></TabsContent>
      <TabsContent value="users"><UsersTab /></TabsContent>
      <TabsContent value="thresholds"><MonitorThresholdsPanel /></TabsContent>
      <TabsContent value="training"><TrainingTab /></TabsContent>
    </Tabs>
  </div>
);

export default MonitoringControlsPanel;
